import { FC } from "react";
import {
  RegisterOptions,
  useController,
  Control,
  FieldValues,
  Path,
} from "react-hook-form";
import SelectDropdown from "@components/SelectDropdown";

interface SelectContainerProps<ItemType, TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  title?: string;
  placeholder?: string;
  disabled?: boolean;
  disabledMessage?: string;
  hasError?: boolean;
  errorMessage?: string;
  items: ItemType[];
  keyExtractor: (item: ItemType) => string;
  labelOptionItem?: (item: ItemType | null) => string;
  renderSelectedItem?: (props: { item: ItemType | null }) => React.ReactNode;
  labelSelectedItem?: (item: ItemType | null) => string;
  renderItem?: (props: { item: ItemType, search?: string, isSelected?: boolean }) => React.ReactNode;
  ItemComponent?: FC<{ item: ItemType, search?: string, isSelected?: boolean }>;
  searchable?: boolean;
  isLoading?: boolean;
  onOpen?: () => void;
  searchHandler?: (v: string) => void;
  footer?: React.ReactNode;
  onBlur?: () => void;
  onChange?: (id: string) => void;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

const SelectContainer = <ItemType extends object = { id: string, value: string }, TFieldValues extends FieldValues = FieldValues>(
  props: SelectContainerProps<ItemType, TFieldValues>,
) => {

  const { field, fieldState } = useController<TFieldValues>({
    control: props.control,
    name: props.name,
    rules: props.rules,
  });

  return (
    <SelectDropdown<ItemType>
      label={props.title}
      placeholder={props.placeholder}
      errorMessage={fieldState.error?.message || props.errorMessage}
      hasError={Boolean(fieldState.error || props.hasError)}
      disabled={props.disabled}
      disabledMessage={props.disabledMessage}
      defaultSelectedItem={field.value}
      labelSelectedItem={props.labelSelectedItem}
      renderSelectedItem={props.renderSelectedItem}
      renderItem={props.renderItem}
      ItemComponent={props.ItemComponent}
      items={props.items}
      keyExtractor={props.keyExtractor}
      onItemSelect={(id: string) => {
        field.onChange(id);
        props.onChange && props?.onChange(id);
      }}
      onBlur={() => {
        field.onBlur();
        props.onBlur && props?.onBlur();
      }}
      value={field.value}
      searchable={props.searchable}
      labelOptionItem={props.labelOptionItem}
      isLoading={props.isLoading}
      onOpen={props.onOpen}
      searchHandler={props.searchHandler}
      footer={props.footer}
      prefix={props.prefix}
      suffix={props.suffix}
    />
  );
};

export default SelectContainer;
